import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Loader2, X, MessageSquare, User, Image, Paperclip, MoreVertical, Reply, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { subscribeToGroupMessages, sendGroupMessage, uploadImage, softDeleteGroupMessage } from '@/lib/firebase/queries/messages';
import { CHAT_PAGE_SIZE, nextChatLimit, offsetAfterPrepend, shouldLoadOlder } from '@/lib/groups/chatPaging';
import { isGroupMember, isGroupEditor, joinGroup, getGroupName, SubgroupConflictError, SectionNotJoinableError } from '@/lib/firebase/queries/group-members';
import { isOrganizationManager } from '@/lib/firebase/queries/organizations/isOrganizationMember';
import { buildReplyTo, canDeleteChatMessage, isDeletedMessage, replyQuote, type ReplyQuote } from '@/lib/groups/chatMessage';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { membershipConflictMessage, sectionNotJoinableMessage } from '@/lib/groups/membershipConflictCopy';
import { isVirtualMember } from '@/lib/firebase/queries/virtual-membership';
import { format } from 'date-fns';
import { toast } from 'sonner';
import type { Group, Message } from '@/types/support';
import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getUserData } from '@/lib/firebase/auth/getUserData';
import { VoiceNoteBubble } from './VoiceNoteBubble';
import { isVoiceNoteMessage } from '@/lib/groups/voiceNote';
import { markGroupRead } from '@/lib/firebase/queries/groupReads';
import { localizedName } from '@/lib/groups/groupPosts';
import { memberDisplayName, pickMemberNameFields, type MemberNameFields } from '@/lib/groups/memberName';

interface GroupChatProps {
  group: Group;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GroupChat({ group, open, onOpenChange }: GroupChatProps) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [joining, setJoining] = useState(false);
  const [userAvatars, setUserAvatars] = useState<Record<string, string>>({});
  const [userNames, setUserNames] = useState<Record<string, MemberNameFields>>({});
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const { t, language } = useLanguage();
  const { currentUser, userData } = useAuth();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // Paging (lib/groups/chatPaging, same as mobile): the newest page live;
  // scrolling to the top widens the listener by one page and keeps the
  // reader on the message they were looking at.
  const [messageLimit, setMessageLimit] = useState(CHAT_PAGE_SIZE);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const hasMoreRef = useRef(false);
  const olderAnchorRef = useRef<{ height: number; offset: number } | null>(null);
  const messageCountRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Sender ids whose profile was already requested. Profiles are fetched once
  // per sender, never from state: a sender without a photo is stored as '',
  // and keying the refetch on state re-subscribed the listener forever.
  const fetchedSenderIdsRef = useRef<Set<string>>(new Set());
  // Reply + soft delete (contract shared with mobile: src/lib/groups/chatMessage.ts).
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Message | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Group editor, creator, or a manager of the group's organization: may
  // delete anyone's message (the same roles that manage members).
  const [isModerator, setIsModerator] = useState(false);
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const longPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusInputOnMenuCloseRef = useRef(false);

  useEffect(() => {
    if (!open || !group.id || !currentUser?.uid) return;
    const uid = currentUser.uid;
    let cancelled = false;
    (async () => {
      let allowed = group.creatorId === uid || !!group.editors?.includes(uid);
      if (!allowed) allowed = await isGroupEditor(group.id, uid);
      if (!allowed && group.organization) allowed = await isOrganizationManager(group.organization, uid);
      if (!cancelled) setIsModerator(allowed);
    })().catch((error) => console.error('Error checking chat moderator:', error));
    return () => {
      cancelled = true;
    };
  }, [open, group.id, group.creatorId, group.editors, group.organization, currentUser?.uid]);

  useEffect(() => {
    if (!open) {
      setReplyingTo(null);
      setPendingDelete(null);
      setMenuOpenFor(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !group.id || !currentUser) return;

    const checkMembership = async () => {
      try {
        console.log('Checking virtual membership for user:', currentUser.uid, 'in group:', group.id);
        // Use virtual membership system - works for both regular groups and sections
        const membershipStatus = await isVirtualMember(group.id, currentUser.uid);
        console.log('Virtual membership status:', membershipStatus);
        setIsMember(membershipStatus);
      } catch (error) {
        console.error('Error checking virtual membership:', error);
        setIsMember(false);
      }
    };

    checkMembership();
  }, [open, group.id, currentUser]);

  useEffect(() => {
    if (!open || !group.id || !isMember) return;

    console.log('Setting up message subscription for group:', group.id);
    // A snapshot that waits on profile fetches must not overwrite a newer one.
    let latestSnapshot = 0;
    const unsubscribe = subscribeToGroupMessages(
      group.id,
      async (newMessages, info) => {
        // The local cache (no info) only fills the first paint; a re-subscribe
        // for an older page must not swap the live list for it.
        if (!info && messageCountRef.current > 0) return;
        hasMoreRef.current = !!info?.hasMore;
        if (olderAnchorRef.current && newMessages.length <= messageCountRef.current) {
          olderAnchorRef.current = null;
          setLoadingOlder(false);
        }
        messageCountRef.current = newMessages.length;
        const snapshot = ++latestSnapshot;
        console.log('Received new messages:', newMessages.length);
        console.log('Messages from users:', newMessages.map(m => m.userId));
        
        // Fetch user data for senders not requested before
        const newUserIds = [...new Set(newMessages.map(m => m.userId))]
          .filter(id => id && !fetchedSenderIdsRef.current.has(id));
        newUserIds.forEach(id => fetchedSenderIdsRef.current.add(id));
        
        if (newUserIds.length > 0) {
          const userDataPromises = newUserIds.map(async (userId) => {
            try {
              const userData = await getUserData(userId);
              return [userId, {
                photoURL: userData?.photoURL || '',
                names: pickMemberNameFields(userData as unknown as Record<string, unknown> | null),
              }] as const;
            } catch (error) {
              console.error('Error fetching user data:', error);
              return [userId, {
                photoURL: '',
                names: {} as MemberNameFields,
              }] as const;
            }
          });
          
          const newUserData = Object.fromEntries(await Promise.all(userDataPromises));
          setUserAvatars(prev => ({ ...prev, ...Object.fromEntries(Object.entries(newUserData).map(([id, data]) => [id, data.photoURL])) }));
          setUserNames(prev => ({ ...prev, ...Object.fromEntries(Object.entries(newUserData).map(([id, data]) => [id, data.names])) }));
        }
        
        if (snapshot !== latestSnapshot) return;
        setMessages(newMessages);
        setLoading(false);
        if (!olderAnchorRef.current) scrollToBottom();
      },
      (error) => {
        console.error('Error in message subscription:', error);
        setLoading(false);
        setMessages([]);
        olderAnchorRef.current = null;
        setLoadingOlder(false);
      },
      messageLimit
    );

    if (!olderAnchorRef.current) scrollToBottom();

    return () => {
      console.log('Cleaning up message subscription');
      unsubscribe();
    };
  }, [open, group.id, isMember, messageLimit]);

  // Another group: start again from its newest page.
  useEffect(() => {
    hasMoreRef.current = false;
    messageCountRef.current = 0;
    olderAnchorRef.current = null;
    setLoadingOlder(false);
    setMessageLimit(CHAT_PAGE_SIZE);
  }, [group.id]);

  // Near the top with more to show: widen by one page.
  useEffect(() => {
    if (!open || loading) return;
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (!viewport) return;
    const onScroll = () => {
      if (!shouldLoadOlder({ offsetY: viewport.scrollTop, hasMore: hasMoreRef.current, loading: !!olderAnchorRef.current })) return;
      olderAnchorRef.current = { height: viewport.scrollHeight, offset: viewport.scrollTop };
      setLoadingOlder(true);
      setMessageLimit((l) => nextChatLimit(l));
    };
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => viewport.removeEventListener('scroll', onScroll);
  }, [open, loading]);

  // Function to handle scrolling to bottom
  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  };

  // Server read state (namat#36 P2): reading the open chat, including
  // messages that arrive while it is open, clears its unread badge on web
  // and mobile.
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : '';
  useEffect(() => {
    if (!open || !isMember || !currentUser?.uid || !group.id) return;
    void markGroupRead(currentUser.uid, group.id);
  }, [open, isMember, currentUser?.uid, group.id, lastMessageId]);

  // Scroll to bottom when messages change — unless an older page was just
  // added above, then stay on the message the reader was looking at.
  useLayoutEffect(() => {
    const anchor = olderAnchorRef.current;
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (anchor && viewport) {
      if (viewport.scrollHeight > anchor.height) {
        viewport.scrollTop = offsetAfterPrepend(anchor, viewport.scrollHeight);
        olderAnchorRef.current = null;
        setLoadingOlder(false);
      }
      return;
    }
    scrollToBottom();
  }, [messages]);

  const handleJoinGroup = async () => {
    if (!currentUser) return;

    try {
      setJoining(true);
      const success = await joinGroup(group.id, currentUser.uid);
      if (success) {
        setIsMember(true);
        toast.success(t('support.joinedGroup'));
      } else {
        toast.error(t('support.errorJoiningGroup'));
      }
    } catch (error) {
      console.error('Error joining group:', error);
      // namat#30: one group per organization — say so instead of a generic failure.
      if (error instanceof SubgroupConflictError) {
        toast.error(membershipConflictMessage('self', language === 'ar', await getGroupName(error.existingGroupId)));
      } else if (error instanceof SectionNotJoinableError) {
        toast.error(sectionNotJoinableMessage(language === 'ar'));
      } else {
        toast.error(t('support.errorJoiningGroup'));
      }
    } finally {
      setJoining(false);
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('support.imageTypeError'));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('support.imageSizeError'));
        return;
      }

      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    if ((!message.trim() && !selectedImage) || !currentUser || !userData || !isMember) return;

    try {
      setSending(true);
      setUploadingImage(!!selectedImage);
      
      let imageUrl = '';
      
      // Upload image if selected
      if (selectedImage) {
        try {
          imageUrl = await uploadImage(selectedImage, group.id, currentUser.uid);
        } catch (error) {
          console.error('Error uploading image:', error);
          toast.error(t('support.imageUploadError'));
          setSending(false);
          setUploadingImage(false);
          return;
        }
      }

      const messageText = message.trim();
      // UserData has no firstName/lastName: this used to store "undefined undefined".
      const senderName = localizedName(userData, language, currentUser.displayName || 'User');
      const replySource = replyingTo;
      const replyTo = replySource
        ? buildReplyTo(replySource, getDisplayName(replySource.userId) || replySource.userName)
        : null;
      setMessage(''); // Clear input immediately for better UX
      removeSelectedImage(); // Clear selected image
      setReplyingTo(null);
      
      console.log('Sending message:', {
        text: messageText,
        imageUrl,
        userId: currentUser.uid,
        userName: senderName,
        groupId: group.id
      });
      
      const success = await sendGroupMessage({
        text: messageText,
        imageUrl: imageUrl && imageUrl.trim() ? imageUrl : undefined,
        userId: currentUser.uid,
        userName: senderName,
        groupId: group.id,
        replyTo
      });

      if (!success) {
        console.error('Failed to send message');
        toast.error(t('support.messageSendError'));
        setMessage(messageText); // Restore message if sending failed
        setReplyingTo(replySource);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('support.messageSendError'));
    } finally {
      setSending(false);
      setUploadingImage(false);
    }
  };

  // The sender's profile name in the UI language (Arabic first in Arabic,
  // falling back to the other language). '' until the profile has loaded;
  // callers fall back to the name stored on the message.
  const getDisplayName = (userId: string) => memberDisplayName(userNames[userId], language);

  const messagesById = new Map(messages.map((m) => [m.id, m]));

  const startReply = (msg: Message) => {
    setReplyingTo(msg);
    setMenuOpenFor(null);
    // The menu's close hands focus to the input instead of its trigger
    // (onCloseAutoFocus below), so Enter sends the reply.
    focusInputOnMenuCloseRef.current = true;
  };

  const handleConfirmDelete = async () => {
    const target = pendingDelete;
    if (!target || !currentUser || !canDeleteChatMessage(target, currentUser.uid, isModerator)) return;
    setDeleting(true);
    const ok = await softDeleteGroupMessage(target.id, currentUser.uid);
    setDeleting(false);
    setPendingDelete(null);
    if (!ok) {
      toast.error(t('support.deleteMessageError'));
      return;
    }
    if (replyingTo?.id === target.id) setReplyingTo(null);
  };

  const quoteLine = (quote: ReplyQuote) => {
    if (quote.kind === 'deleted') return t('support.messageDeleted');
    if (quote.kind === 'voice') return t('support.replyVoice');
    if (quote.kind === 'image') return quote.text ? `📷 ${quote.text}` : t('support.replyPhoto');
    return quote.text;
  };

  /** One-line preview of a message for the reply bar above the input. */
  const replyBarLine = (msg: Message) => {
    const reply = buildReplyTo(msg, '');
    return reply ? quoteLine(replyQuote(reply)!) : t('support.messageDeleted');
  };

  const scrollToMessage = (messageId: string) => {
    const el = scrollAreaRef.current?.querySelector(`[data-message-id="${CSS.escape(messageId)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  // Long-press (touch) opens the same menu the hover button opens.
  const startLongPress = (messageId: string) => {
    cancelLongPress();
    longPressRef.current = setTimeout(() => {
      longPressRef.current = null;
      setMenuOpenFor(messageId);
    }, 500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-screen h-[100dvh] sm:w-[90vw] sm:max-w-4xl sm:h-[85vh] flex flex-col p-0"
        hideCloseButton
        dir={language === 'ar' ? 'rtl' : 'ltr'}
      >
        <DialogHeader className={cn(
          "px-6 py-4 border-b flex flex-row items-center justify-between",
          language === 'ar' ? 'flex-row-reverse' : ''
        )}>
          <div className={cn(
            "flex items-center gap-2",
            language === 'ar' ? 'flex-row-reverse ml-auto mr-[5px]' : ''
          )}>
            <DialogTitle className={cn(
              "text-xl",
              language === 'ar' ? 'text-right' : 'text-left'
            )}>
              {t('support.chatWith', { group: group.name })}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Chat messages for {group.name}
            </DialogDescription>
            <MessageSquare className="h-6 w-6 text-[#7dda55]" />
          </div>
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('common.close')}
              className={cn(
                'absolute top-2',
                language === 'ar' ? 'left-4' : 'right-4'
              )}
            >
              <span className="sr-only">{t('common.close')}</span>
              <X className="h-5 w-5" />
            </Button>
          </DialogClose>
        </DialogHeader>

        {!isMember ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <p className="text-muted-foreground mb-4">{t('support.joinToChat')}</p>
            <Button
              onClick={handleJoinGroup}
              disabled={joining}
              className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
            >
              {joining ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('support.joining')}
                </>
              ) : (
                t('support.joinGroup')
              )}
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea 
              ref={scrollAreaRef}
              className="flex-1 p-6"
            >
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-muted-foreground">
                  {t('support.startConversation')}
                </div>
              ) : (
                <div className="space-y-4">
                  {loadingOlder && (
                    <div className="flex justify-center py-2" data-testid="chat-loading-older">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {messages.map((msg) => {
                    const isOwn = msg.userId === currentUser?.uid;
                    const deleted = isDeletedMessage(msg);
                    const canDelete = canDeleteChatMessage(msg, currentUser?.uid, isModerator);
                    const quote = deleted || !msg.replyTo
                      ? null
                      : replyQuote(msg.replyTo, messagesById.get(msg.replyTo.id));
                    const avatar = (
                      <Avatar className="w-8 h-8 mt-1">
                        <AvatarImage
                          src={userAvatars[msg.userId]}
                          alt={getDisplayName(msg.userId) || msg.userName}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <AvatarFallback className="bg-[#7dda55] text-white">
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                    );
                    const menu = deleted ? null : (
                      <DropdownMenu
                        dir={language === 'ar' ? 'rtl' : 'ltr'}
                        open={menuOpenFor === msg.id}
                        onOpenChange={(next) => setMenuOpenFor(next ? msg.id : null)}
                      >
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t('support.messageActions')}
                            className={cn(
                              'h-7 w-7 self-center shrink-0 transition-opacity',
                              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
                              '[@media(hover:none)]:opacity-60'
                            )}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align={isOwn ? 'end' : 'start'}
                          onCloseAutoFocus={(e) => {
                            if (!focusInputOnMenuCloseRef.current) return;
                            focusInputOnMenuCloseRef.current = false;
                            e.preventDefault();
                            inputRef.current?.focus();
                          }}
                        >
                          <DropdownMenuItem onSelect={() => startReply(msg)} className="gap-2">
                            <Reply className="h-4 w-4" />
                            {t('support.chatReply')}
                          </DropdownMenuItem>
                          {canDelete && (
                            <DropdownMenuItem
                              onSelect={() => setPendingDelete(msg)}
                              className="gap-2 text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                              {t('support.chatDelete')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                    return (
                    <div
                      key={msg.id}
                      data-message-id={msg.id}
                      className={cn(
                        "group flex gap-2",
                        isOwn ? 'justify-end' : 'justify-start'
                      )}
                    >
                      {!isOwn && avatar}
                      {isOwn && menu}
                      <div
                        className={cn(
                          "max-w-[80%] rounded-lg p-3",
                          deleted
                            ? 'bg-muted/60 text-muted-foreground'
                            : isOwn
                              ? 'bg-[hsl(99,76%,91%)] text-namat-text'
                              : 'bg-muted',
                          language === 'ar' && !isOwn ? 'text-left' : ''
                        )}
                        onContextMenu={deleted ? undefined : (e) => {
                          e.preventDefault();
                          setMenuOpenFor(msg.id);
                        }}
                        onTouchStart={deleted ? undefined : () => startLongPress(msg.id)}
                        onTouchEnd={cancelLongPress}
                        onTouchMove={cancelLongPress}
                        onTouchCancel={cancelLongPress}
                      >
                        <div className={cn(
                          "flex items-center gap-2 mb-1",
                          language === 'ar' && isOwn ? 'flex-row-reverse' :
                          language === 'ar' && !isOwn ? 'flex-row' : ''
                        )}>
                          <span className="text-sm font-medium">
                            {getDisplayName(msg.userId) || msg.userName}
                          </span>
                          <span className="text-xs opacity-70">
                            {format(msg.createdAt, 'p')}
                          </span>
                        </div>
                        {deleted ? (
                          <p className="text-sm italic text-muted-foreground">
                            {t('support.messageDeleted')}
                          </p>
                        ) : (
                          <>
                            {quote && msg.replyTo && (
                              <button
                                type="button"
                                onClick={() => scrollToMessage(msg.replyTo!.id)}
                                className={cn(
                                  'mb-2 block w-full rounded-md border-s-4 border-[#7dda55] bg-black/5 px-2 py-1',
                                  language === 'ar' ? 'text-right' : 'text-left'
                                )}
                              >
                                <span className="block text-xs font-semibold text-[#4a9a2c]">
                                  {getDisplayName(msg.replyTo.userId) || msg.replyTo.userName}
                                </span>
                                <span className={cn(
                                  'block text-xs opacity-80 line-clamp-2 break-words',
                                  quote.kind === 'deleted' ? 'italic' : ''
                                )}>
                                  {quoteLine(quote)}
                                </span>
                              </button>
                            )}
                            {msg.imageUrl && (
                              <div className="mb-2">
                                <img
                                  src={msg.imageUrl}
                                  alt="Message image"
                                  className="max-w-full max-h-64 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(msg.imageUrl, '_blank')}
                                />
                              </div>
                            )}
                            {isVoiceNoteMessage(msg) && (
                              msg.audioUrl ? (
                                <div className="mb-1">
                                  <VoiceNoteBubble
                                    src={msg.audioUrl}
                                    duration={msg.audioDuration}
                                    isOwn={isOwn}
                                    language={language}
                                  />
                                </div>
                              ) : (
                                <p className="text-sm italic opacity-70">
                                  {language === 'ar' ? 'رسالة صوتية غير متاحة' : 'Voice note unavailable'}
                                </p>
                              )
                            )}
                            {msg.text && (
                              <p className={cn(
                                "text-sm whitespace-pre-wrap",
                                language === 'ar' && !isOwn ? 'text-left' : ''
                              )}>{msg.text}</p>
                            )}
                          </>
                        )}
                      </div>
                      {!isOwn && menu}
                      {isOwn && avatar}
                    </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            <div className="p-4 border-t">
              {/* Reply bar */}
              {replyingTo && (
                <div className="mb-3 flex items-start gap-2 rounded-md border-s-4 border-[#7dda55] bg-muted px-3 py-2">
                  <Reply className="h-4 w-4 mt-0.5 shrink-0 text-[#4a9a2c]" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-[#4a9a2c]">
                      {t('support.replyingTo', {
                        name: getDisplayName(replyingTo.userId) || replyingTo.userName,
                      })}
                    </p>
                    <p className="text-xs opacity-80 truncate">{replyBarLine(replyingTo)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label={t('support.cancelReply')}
                    onClick={() => setReplyingTo(null)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}

              {/* Image Preview */}
              {imagePreview && (
                <div className="mb-3 relative">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="max-w-32 max-h-32 rounded-lg object-cover"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 text-white hover:bg-red-600"
                    onClick={removeSelectedImage}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  if ((message.trim() || selectedImage) && !sending) {
                    handleSend();
                  }
                }}
                className="flex gap-2"
              >
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                
                {/* Image upload button */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  className="flex-shrink-0"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>

                <Input
                  ref={inputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('support.typeMessage')}
                  className="flex-1 text-base"
                  style={{ fontSize: '16px' }}
                  disabled={sending}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && replyingTo) {
                      // Cancel the reply, not the whole chat dialog.
                      e.preventDefault();
                      e.stopPropagation();
                      setReplyingTo(null);
                      return;
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if ((message.trim() || selectedImage) && !sending) {
                        handleSend();
                      }
                    }
                  }}
                />
                <Button 
                  type="submit"
                  disabled={sending || (!message.trim() && !selectedImage)}
                  className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : uploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </div>
          </>
        )}

        <AlertDialog
          open={pendingDelete !== null}
          onOpenChange={(next) => {
            if (!next && !deleting) setPendingDelete(null);
          }}
        >
          <AlertDialogContent dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <AlertDialogHeader>
              <AlertDialogTitle className={language === 'ar' ? 'text-right' : 'text-left'}>
                {t('support.deleteMessageTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription className={language === 'ar' ? 'text-right' : 'text-left'}>
                {t('support.deleteMessageConfirm')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className={language === 'ar' ? 'sm:flex-row-reverse sm:justify-start gap-2' : ''}>
              <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={(e) => {
                  e.preventDefault();
                  void handleConfirmDelete();
                }}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t('support.chatDelete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}